import React, { Component } from 'react';
import { connect } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Grid, Paper, Tooltip, Typography } from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';
import { monthsList } from '../../helpers/constants';
import StationStatisticsPortfolioSummary from './StationStatisticsPortfolioSummary';
 
import * as statisticsReducers from '../reducer';
import { gradientColors } from '../../helpers/constants'; // Import gradientColors
import '../../index.css';
 
Highcharts.setOptions({
  chart: {
      style: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
      }
  }
});
 
class StationStatisticsPortfolioGraph extends Component {
 
  state = {
    chartOptions: {
      chart: {
        type: 'column',
        height: 700,   // height of chart
        marginBottom: 100
      },
      title: {
        text: '',  
      },
      subtitle: {
        text: '',  
      },
      xAxis: {
        categories: [], // Time categories (year/month)
        title: {
          text: 'Time',
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Energy Yields',
        },
        stackLabels: {
          enabled: true,
          useHTML: true,
          zIndex: 0, // Ensure stack labels are behind the tooltip
          formatter: function () {
            const index = this.x; // Use the x position as the index
            const portfolioSummary = this.axis.options.portfolioSummary; // Use portfolioSummary from yAxis options
            if (portfolioSummary && portfolioSummary[index]) {
              const { avgPerformanceRatio, totalEnergyYields, totalSystemCapacity, weightagePerformanceRatio  } = portfolioSummary[index];
         
              return `<div style="text-align: center; font-size: 0.75rem; color: gray;">
              Total Average PR: ${avgPerformanceRatio || 0}%<br>
              Total Weightage PR: ${weightagePerformanceRatio || 0}%<br>
              Energy Yields: ${StationStatisticsPortfolioGraph.formatEnergyYields(totalEnergyYields)}<br>
              System Cap: ${StationStatisticsPortfolioGraph.formatSystemCapacity(totalSystemCapacity)}
              </div>`;
 
            }
            return '';
          },          
        },
       
      },
      tooltip: {
        useHTML: true,
        borderRadius: 6,
        borderWidth: 1,
        shadow: true,
        style: {
          zIndex: 9999,
          padding: '10px',
          fontSize: '13px',
          color: '#333',
          backgroundColor: '#ffffff',
          opacity: 1,
        },
        positioner(labelWidth, labelHeight, point) {
          return {
            x: point.plotX + this.chart.plotLeft - labelWidth - 70,
            y: point.plotY + this.chart.plotTop - labelHeight / 2,
          };
        },
        formatter() {
          const { name: stationName } = this.series;
          const { y: energyYields, performanceRatio = 0, performanceRatioWeightage = 0, systemCapacity } = this.point;
     
          const formattedEnergyYields = StationStatisticsPortfolioGraph.formatEnergyYields(energyYields);
          const formattedSystemCapacity = systemCapacity
            ? StationStatisticsPortfolioGraph.formatSystemCapacity(systemCapacity)
            : 'N/A';
     
          return `
            <strong>${stationName}</strong><br>
            Average PR: ${performanceRatio}%<br>
            Weightage Ratio: ${performanceRatioWeightage}%<br>
            Energy: ${formattedEnergyYields}<br>
            System Cap: ${formattedSystemCapacity}
          `;
        },
      },
      legend: {
        enabled: true,
        itemStyle: {
          cursor: 'default' // prevents hand pointer
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            formatter: function () {
              // Display only the numeric value, no units
              return this.y.toLocaleString();
            },
          },
          groupPadding: 0.1, // Adjust group padding for better bar spacing
          pointPadding: 0.1, // Adjust point padding for consistent spacing
          pointWidth: 55, // Automatically adjust width based on available space
        },
      },
      series: {
        events: {
          legendItemClick: function () {
            return false;
          }
        }
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: ['viewFullscreen'] // only this item will show
          }
        }
      }
    },
  };
 
  // Reusable static method for energy yields formatting
  // static formatEnergyYields(value) {
  //   const formattedValue = value.toLocaleString('de-DE'); // German-style formatting (1.000.000)
  //   return value >= 1000000
  //     ? `${formattedValue} MWh`  // Use dot as thousands separator for MWh
  //     : `${formattedValue} kWh`; // Use dot for kWh as well
  // }
 
  static formatEnergyYields = (value) => {
    return value >= 1000000
      ? `${value.toLocaleString('de-DE')} MWh`  // MWh uses dot as separator (German format)
      : `${value.toLocaleString('en-US')} kWh`; // kWh uses comma (US format)
  };
 
  static formatSystemCapacity = (value) => {
    return `${value.toLocaleString()} kWp`; // Always display as kWp
  };
  // Format System Capacity with proper units (kWp if < 1000, MWp if ≥ 1000)
  // static formatSystemCapacity = (value) => {
  //   return value < 1000
  //     ? `${value.toLocaleString()} kWp`  // For values less than 1000, show as kWp
  //     : `${value.toLocaleString()} MWp`; // For values 1000 and above, show as MWp with commas
  // };
  componentDidMount() {
    this.initializeChart();
  }
 
  componentDidUpdate(prevProps) {
    if (prevProps.portfolioData !== this.props.portfolioData ||
        prevProps.portfolioDetails.stationList !== this.props.portfolioDetails.stationList) {
      this.initializeChart();
    }
  }
 
 
  getGradientColor(index) {
    const gradientColor = gradientColors[index % gradientColors.length];
    return {
      linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
      stops: [
        [0, gradientColor[0]], // Light color
        [1, gradientColor[1]], // Dark color
      ],
    };
  }
 
  initializeChart = () => {
    const { portfolioData, portfolioDetails, selectedPortfolioYear } = this.props;
    if (!portfolioData || !portfolioDetails) return;
 
    const categories = [];
    const series = [];
    const portfolioLevel = portfolioData.portfolioLevel || 'Portfolio';
    const portfolioSummary = portfolioData.portfolioSummary || [];
    const portfolioDetailsList = portfolioData.portfolioDetails || [];
    const stationList = portfolioDetails.stationList || [];
 
    // Format station list for display
    // const StationStatisticsPortfolioSummary = stationList.length > 0
    //   ? stationList.join(', ')
    //   : 'No stations available';
 
    // Extract station descriptions instead of names
    const stationDetailsList = portfolioDetails.stationDetails || [];
    const StationStatisticsPortfolioSummary = stationDetailsList.length > 0
    ? stationDetailsList.map((station) =>
        `<span style="display: inline-block; padding: 5px; margin: 1px 2px 1px 0; border: 1px solid #ccc; border-radius: 15px; background-color: #f0f0f0; font-size: 0.85rem;">
          ${station.description}
        </span>`
      ).join(' ')
    : 'No stations available';
 
    // Set categories based on time (year or month)
    portfolioSummary.forEach((entry) => {
      if (entry.time.includes('-')) {
        const [year, month] = entry.time.split('-');
        const monthName = monthsList[parseInt(month, 10) - 1]?.name || '';
        categories.push(`${monthName} ${year}`); // Example: "Jan 2024"
      } else {
        categories.push(entry.time); // Example: "2024"
      }
    });
 
    // Group data by project/city/country based on portfolioLevel
    const groupingKey = portfolioData.portfolioLevel; // Example: "project", "city", "country"
    const groupedData = portfolioDetailsList.reduce((acc, detail) => {
      const key = detail[groupingKey] || 'Unknown';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push({
        time: detail.time,
        energyYields: detail.energyYields,
        performanceRatio: detail.performanceRatio,
        performanceRatioWeightage: detail.performanceRatioWeightage,
        systemCapacity: detail.systemCapacity || 0,
      });
      return acc;
    }, {});
 
    // Create series for each group
    Object.keys(groupedData).forEach((group, index) => {
      const data = categories.map((category) => {
        const year = category.split(' ')[1] || category;
        const month = category.split(' ')[0];
 
        const monthObj = monthsList.find(m => m.name === month);
        const formattedMonth = monthObj ? String(monthObj.value).padStart(2, '0') : '';
 
        const entry = groupedData[group].find(
          (d) =>
            d.time === year || // Match full year like "2024"
            d.time === `${year}-${formattedMonth}` // Match month like "2024-01"
        );
   
        return entry
        ? {
            y: entry.energyYields,
            performanceRatio: entry.performanceRatio,
            performanceRatioWeightage: entry.performanceRatioWeightage,
            systemCapacity: entry.systemCapacity || 0
          }
        : { y: 0, performanceRatio: 0, performanceRatioWeightage: 0, systemCapacity: 0 };
      });
 
      series.push({
        name: group,
        data,
        color: this.getGradientColor(index),
        events: {
          legendItemClick: function () {
            return false; // Disable toggling series from legend
          }
        }
      });
    });
 
    // Determine xAxis title based on selectedPortfolioYear
    const xAxisTitle = selectedPortfolioYear === 'all' ? 'Year' : 'Month';
 
    // Update chart options
    this.setState((prevState) => ({
      chartOptions: {
        ...prevState.chartOptions,
        xAxis: {
          ...prevState.chartOptions.xAxis,
          categories,
          title: {
            text: xAxisTitle,
          },
        },
        yAxis: {
          ...prevState.chartOptions.yAxis,
          portfolioSummary,  // Ensure portfolioSummary is accessible
        },
        series,
      },
    }));
  };  
 
  render() {
    const { chartOptions } = this.state;
    const { portfolioData, portfolioDetails } = this.props;
    const portfolioLevel = portfolioData?.portfolioLevel || 'Portfolio';
    const loading = false; // Set this based on your loading state
    const hasData = portfolioData?.portfolioSummary?.length > 0;
 
    let message = '';
    if (
      !portfolioData ||
      !portfolioData.portfolioSummary ||
      !portfolioData.portfolioDetails ||
      portfolioData.portfolioSummary.length === 0 ||
      portfolioData.portfolioDetails.length === 0
    ) {
      message = "Portfolio data isn't available. Please adjust your filters.";
    }
 
    return (
      <Grid container alignItems="center">
        <Grid item xs={12} style={{ margin: '1px 10px' }}>
          {message ? (
            <Paper style={{ padding: 20, textAlign: 'center' }}>
              <p style={{ margin: 0, color: '#999', fontSize: '16px' }}>{message}</p>
            </Paper>
          ) : (
            <Paper>
              <Typography className="portfolioFaultTitle" variant="h6" color="primary">
                Portfolio Statistics Overview
              </Typography>
             
              <StationStatisticsPortfolioSummary
                stations={portfolioDetails?.stationDetails || []}
                portfolioLevel={portfolioLevel}
              />
 
              <div style={{ width: '100%', height: '100%' }}>
                {!loading && hasData ? (
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions}
                    immutable={true}
                    containerProps={{ style: { width: '100%', height: '100%' } }}
                  />
                ) : (
                  <Typography variant="subtitle1" style={{ margin: '20px' }}>
                    No data available. Please submit to see the statistics.
                  </Typography>
                )}
              </div>
            </Paper>
          )}
        </Grid>
      </Grid>
    );
  }
}
 
const mapStateToProps = (state) => ({
  portfolioData: statisticsReducers.getPortfolioData(state),
  portfolioDetails: statisticsReducers.getPortfolioDetails(state),
  selectedPortfolioYear: statisticsReducers.getSelectedPortfolioYear(state),
});
 
export default connect(mapStateToProps)(StationStatisticsPortfolioGraph);